import { ASSIGNED_SELECT_NONE } from "@sd/lib/adminTicketUtils";
import {
  normalizeCreatedDateFilter,
  type CreatedDateFilterState,
} from "@sd/lib/ticketCreatedDateFilter";
import { normalizeCategoryFilterValues } from "@sd/lib/ticketCategoryOptions";
import { normalizeMultiFilterValues } from "@sd/lib/ticketFilterApi";
import {
  sanitizeStatusFilterValues,
  statusToApiValue,
} from "@sd/constants/ticketStatuses";

export const DEFAULT_TICKET_CREATE_LIST_PAGE = 1;
export const DEFAULT_TICKET_CREATE_LIST_PAGE_SIZE = 100;

export interface TicketToolbarFilterInput {
  search?: string;
  page?: number;
  pageSize?: number;
  statusFilters?: string[];
  priorityFilters?: string[];
  categoryFilters?: string[];
  typeFilter?: string;
  projectFilters?: string[];
  assigneeFilters?: string[];
  unassignedFilterValue?: string;
  requesterFilters?: string[];
  createdDateFilter?: CreatedDateFilterState;
  /**
   * Admin / Super Agent date-axis filter.
   * `"all"` → omit axis; `"on_create"` → `on_create=true`; `"on_update"` → `on_update=true`.
   */
  createUpdateFilter?: "all" | "on_create" | "on_update";
}

/**
 * Query params for GET `/service_desk/ticket_create/` (Postman-aligned).
 * Ticket type filter: `type=issue` (response field is `tickettype`).
 * Multiple statuses: `status=closed,new,feedback`
 * Created date: `start_date` / `end_date` (`YYYY-MM-DD`)
 */
export function buildTicketCreateListFilterParams(
  input: TicketToolbarFilterInput & {
    extra?: Record<string, unknown>;
    isMerge?: boolean;
  }
): Record<string, unknown> {
  const params: Record<string, unknown> = {
    is_merge: input.isMerge ?? false,
    search: input.search?.trim() ?? "",
    page: input.page ?? DEFAULT_TICKET_CREATE_LIST_PAGE,
    page_size: input.pageSize ?? DEFAULT_TICKET_CREATE_LIST_PAGE_SIZE,
  };

  if (input.extra) {
    Object.assign(params, input.extra);
  }

  const statuses = sanitizeStatusFilterValues(
    normalizeMultiFilterValues(input.statusFilters ?? [])
  );
  if (statuses.length > 0) {
    params.status = statuses.map(statusToApiValue).join(",");
  }

  const priorityIds = normalizeMultiFilterValues(input.priorityFilters ?? []);
  if (priorityIds.length > 0) {
    params.priority_id = priorityIds.join(",");
  }

  const categories = normalizeCategoryFilterValues(input.categoryFilters ?? []);
  if (categories.length > 0) {
    params.category = categories.join(",");
  }

  if (input.typeFilter && input.typeFilter !== "all") {
    params.type = input.typeFilter;
  }

  const projects = normalizeMultiFilterValues(input.projectFilters ?? []);
  if (projects.length > 0) {
    params.project = projects.join(",");
  }

  const assignees = normalizeMultiFilterValues(input.assigneeFilters ?? []);
  const unassignedSentinel = input.unassignedFilterValue ?? ASSIGNED_SELECT_NONE;
  const assigneeIds = assignees.filter(id => id !== unassignedSentinel);
  const includeUnassigned = assignees.includes(unassignedSentinel);
  if (assigneeIds.length > 0) {
    params.assigned_to = assigneeIds.join(",");
  }
  if (includeUnassigned) {
    params.assigned_to__isnull = true;
  }

  const requesters = normalizeMultiFilterValues(input.requesterFilters ?? []);
  if (requesters.length > 0) {
    params.requester = requesters.join(",");
  }

  const dates = normalizeCreatedDateFilter(input.createdDateFilter);
  if (dates.from) params.start_date = dates.from;
  if (dates.to) params.end_date = dates.to;

  if (input.createUpdateFilter === "on_update") {
    params.on_update = true;
  } else if (input.createUpdateFilter === "on_create") {
    params.on_create = true;
  }

  return params;
}
